import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getQAAlerts, getQAAlertsSummary, reviewAlert, bulkReviewAlerts,
  overrideAlertCompliance, archiveReviewedAlerts, getQAActivities,
  getAuditorPerformanceReport, getMyAssignedUsers, getCalls,
  bulkOverrideAlerts, getUsers, getQATasks, createQATask, updateQATask,
  completeQATask, getUserAssignments, createUserAssignment, deleteQATask,
  getAlertsCategoryStats, qaReviewCall, overrideCall, getAvatarDisplayUrl
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertTriangle, AlertOctagon, CheckCircle2, XCircle, Clock, Archive,
  Eye, Edit, MoreVertical, Filter, RefreshCw, Search,
  Users, Phone, FileText, TrendingUp, Activity, Shield, ShieldX,
  ChevronLeft, ChevronRight, Loader2, BarChart3, CalendarDays,
  History, ThumbsUp, ThumbsDown, MessageSquare, Download,
  UserCheck, ClipboardList, ClipboardCheck, Target, Plus, Play, Pause,
  ChevronDown, X, Trash2, PhoneCall, ListFilter, Star, UserPlus
} from 'lucide-react';

// ==================== CONSTANTS ====================
const PRIORITY_COLORS = {
  low: 'bg-gray-500',
  medium: 'bg-blue-500',
  high: 'bg-amber-500',
  urgent: 'bg-red-500'
};

const STATUS_COLORS = {
  pending: 'bg-gray-500',
  in_progress: 'bg-blue-500',
  completed: 'bg-emerald-500',
  overdue: 'bg-red-500'
};

// ==================== ALERT REVIEW DIALOG ====================
function AlertReviewDialog({ open, onClose, alert, onReview, onOverride }) {
  const [action, setAction] = useState('review');
  const [qaNotes, setQaNotes] = useState('');
  const [newCompliance, setNewCompliance] = useState(true);
  const [overrideReason, setOverrideReason] = useState('');
  const [adjustScore, setAdjustScore] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && alert) {
      setAction('review');
      setQaNotes('');
      setNewCompliance(true);
      setOverrideReason('');
      setAdjustScore(true);
    }
  }, [open, alert]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (action === 'review') {
        await onReview(alert.id, qaNotes);
        toast.success('Alert marked as reviewed');
      } else {
        if (!overrideReason.trim()) {
          toast.error('Override reason is required');
          setLoading(false);
          return;
        }
        await onOverride(alert.id, {
          new_compliance_status: newCompliance,
          override_reason: overrideReason,
          adjust_score: adjustScore,
          qa_notes: qaNotes || null
        });
        toast.success(`Compliance overridden to ${newCompliance ? 'Compliant' : 'Non-Compliant'}`);
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  if (!alert) return null;
  const currentCompliance = alert.has_notes || alert.manually_marked_compliant;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            Review Alert
          </DialogTitle>
          <DialogDescription>
            {alert.user_info?.name || 'Unknown User'} - {new Date(alert.call_date || alert.created_at).toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Current Status:</span>
              <Badge variant={currentCompliance ? 'default' : 'destructive'}>
                {currentCompliance ? 'Compliant' : 'Non-Compliant'}
              </Badge>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              variant={action === 'review' ? 'default' : 'outline'}
              onClick={() => setAction('review')}
              className="flex-1"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Mark as Reviewed
            </Button>
            <Button
              variant={action === 'override' ? 'default' : 'outline'}
              onClick={() => setAction('override')}
              className="flex-1"
            >
              <Edit className="h-4 w-4 mr-2" />
              Override Compliance
            </Button>
          </div>

          {action === 'override' && (
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <Label>New Compliance Status:</Label>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={newCompliance ? 'default' : 'outline'}
                    onClick={() => setNewCompliance(true)}
                    className="gap-1"
                  >
                    <ThumbsUp className="h-4 w-4" />
                    Compliant
                  </Button>
                  <Button
                    size="sm"
                    variant={!newCompliance ? 'destructive' : 'outline'}
                    onClick={() => setNewCompliance(false)}
                    className="gap-1"
                  >
                    <ThumbsDown className="h-4 w-4" />
                    Non-Compliant
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm">Override Reason *</Label>
                <Textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Explain why you're overriding..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Adjust Call Score</Label>
                  <p className="text-xs text-muted-foreground">Update score based on new compliance</p>
                </div>
                <Switch checked={adjustScore} onCheckedChange={setAdjustScore} />
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm">QA Notes (Optional)</Label>
            <Textarea
              value={qaNotes}
              onChange={(e) => setQaNotes(e.target.value)}
              placeholder="Add notes for this review..."
              className="mt-1"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {action === 'review' ? 'Mark Reviewed' : 'Override Compliance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== BULK OVERRIDE DIALOG ====================
function BulkOverrideDialog({ open, onClose, selectedCount, onConfirm }) {
  const [newCompliance, setNewCompliance] = useState(true);
  const [overrideReason, setOverrideReason] = useState('');
  const [adjustScore, setAdjustScore] = useState(true);
  const [qaNotes, setQaNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setNewCompliance(true);
      setOverrideReason('');
      setAdjustScore(true);
      setQaNotes('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!overrideReason.trim() || overrideReason.trim().length < 5) {
      toast.error('Override reason required (min 5 characters)');
      return;
    }
    
    setLoading(true);
    try {
      await onConfirm({
        new_compliance_status: newCompliance,
        override_reason: overrideReason,
        adjust_score: adjustScore,
        qa_notes: qaNotes || null
      });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk override failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-purple-500" />
            Bulk Override Compliance
          </DialogTitle>
          <DialogDescription>
            Override {selectedCount} selected alert{selectedCount > 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              This action will override compliance for all selected alerts.
            </div>
          </div>

          <div className="space-y-2">
            <Label>New Compliance Status</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={newCompliance ? 'default' : 'outline'}
                onClick={() => setNewCompliance(true)}
                className="flex-1 gap-2"
              >
                <ThumbsUp className="h-4 w-4" />
                Compliant
              </Button>
              <Button
                type="button"
                variant={!newCompliance ? 'destructive' : 'outline'}
                onClick={() => setNewCompliance(false)}
                className="flex-1 gap-2"
              >
                <ThumbsDown className="h-4 w-4" />
                Non-Compliant
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Override Reason *</Label>
            <Textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Provide a reason (required)..."
              rows={3}
            />
          </div>

          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label className="text-sm font-medium">Adjust Call Scores</Label>
              <p className="text-xs text-muted-foreground">Recalculate scores for associated calls</p>
            </div>
            <Switch checked={adjustScore} onCheckedChange={setAdjustScore} />
          </div>

          <div className="space-y-2">
            <Label>QA Notes (Optional)</Label>
            <Textarea
              value={qaNotes}
              onChange={(e) => setQaNotes(e.target.value)}
              placeholder="Add notes..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !overrideReason.trim()}
            className={newCompliance ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Override {selectedCount} Alert{selectedCount > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== CREATE TASK DIALOG ====================
function CreateTaskDialog({ open, onClose, auditors, allUsers, onTaskCreated, tenantId, currentUser }) {
  const [loading, setLoading] = useState(false);
  const [taskData, setTaskData] = useState({
    title: '',
    description: '',
    task_type: 'review_calls',
    priority: 'medium',
    assigned_to: '',
    due_date: '',
    related_call_id: '',
    related_user_id: ''
  });

  useEffect(() => {
    if (open) {
      // Reset form
      setTaskData({
        title: '',
        description: '',
        task_type: 'review_calls',
        priority: 'medium',
        assigned_to: '',
        due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Default: 1 week
        related_call_id: '',
        related_user_id: ''
      });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!taskData.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    if (!taskData.assigned_to) {
      toast.error('Please assign the task to an auditor');
      return;
    }
    if (!taskData.due_date) {
      toast.error('Due date is required');
      return;
    }

    setLoading(true);
    try {
      await createQATask(tenantId, {
        title: taskData.title,
        description: taskData.description,
        task_type: taskData.task_type,
        priority: taskData.priority,
        auditor_id: taskData.assigned_to,  // Map assigned_to to auditor_id
        assigned_by: currentUser?.id || '',  // Current user is assigning this task
        due_date: taskData.due_date,
        related_call_id: taskData.related_call_id || null,
        related_user_id: taskData.related_user_id || null,
        tenant_id: tenantId,
        status: 'pending'
      });
      toast.success('Task created successfully');
      onTaskCreated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const TASK_TYPES = [
    { value: 'review_calls', label: 'Review Calls' },
    { value: 'make_qa_notes', label: 'Make QA Notes' },
    { value: 'coaching_session', label: 'Coaching Session' },
    { value: 'review_homework', label: 'Review Homework' },
    { value: 'general', label: 'General' }
  ];

  const PRIORITIES = [
    { value: 'low', label: 'Low', color: 'bg-gray-500' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-500' },
    { value: 'high', label: 'High', color: 'bg-amber-500' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-500' }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-500" />
            Create New Task
          </DialogTitle>
          <DialogDescription>
            Assign a new QA task to an auditor
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label>Task Title *</Label>
            <Input
              value={taskData.title}
              onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
              placeholder="e.g., Review critical calls from last week"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={taskData.description}
              onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
              placeholder="Detailed description of what needs to be done..."
              rows={3}
            />
          </div>

          {/* Task Type & Priority Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Task Type</Label>
              <Select
                value={taskData.task_type}
                onValueChange={(v) => setTaskData({ ...taskData, task_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={taskData.priority}
                onValueChange={(v) => setTaskData({ ...taskData, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${p.color}`} />
                        {p.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assign To */}
          <div className="space-y-2">
            <Label>Assign To *</Label>
            <Select
              value={taskData.assigned_to}
              onValueChange={(v) => setTaskData({ ...taskData, assigned_to: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an auditor" />
              </SelectTrigger>
              <SelectContent>
                {auditors.map(auditor => (
                  <SelectItem key={auditor.id} value={auditor.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs bg-primary/10">
                          {auditor.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      {auditor.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Due Date *</Label>
            <Input
              type="date"
              value={taskData.due_date}
              onChange={(e) => setTaskData({ ...taskData, due_date: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Related User (Optional) */}
          <div className="space-y-2">
            <Label>Related User (Optional)</Label>
            <Select
              value={taskData.related_user_id || "none"}
              onValueChange={(v) => setTaskData({ ...taskData, related_user_id: v === "none" ? "" : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a user to review" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {allUsers.filter(u => u.role_id !== 'quality_auditor' && u.role_id !== 'lead_quality_auditor').slice(0, 50).map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== AUDITOR SELECTOR COMPONENT ====================
function AuditorSelector({ auditors, selectedAuditors, onSelectionChange, isAdmin }) {
  if (!isAdmin || auditors.length === 0) return null;

  const selectedCount = selectedAuditors.length;
  const allSelected = selectedAuditors.length === auditors.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(auditors.map(a => a.id));
    }
  };

  const handleToggleAuditor = (auditorId) => {
    if (selectedAuditors.includes(auditorId)) {
      onSelectionChange(selectedAuditors.filter(id => id !== auditorId));
    } else {
      onSelectionChange([...selectedAuditors, auditorId]);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Users className="h-4 w-4" />
          {selectedCount === 0 ? 'All Auditors' : 
           selectedCount === auditors.length ? 'All Auditors' :
           `${selectedCount} Auditor${selectedCount > 1 ? 's' : ''}`}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <div className="p-2 border-b">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Filter by Auditor</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleSelectAll}
              className="h-7 text-xs"
            >
              {allSelected ? 'Clear All' : 'Select All'}
            </Button>
          </div>
        </div>
        <ScrollArea className="h-64">
          {auditors.map((auditor) => (
            <DropdownMenuCheckboxItem
              key={auditor.id}
              checked={selectedAuditors.includes(auditor.id)}
              onCheckedChange={() => handleToggleAuditor(auditor.id)}
              className="py-2"
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={getAvatarDisplayUrl(auditor)} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                    {auditor.name?.charAt(0) || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{auditor.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{auditor.email}</p>
                </div>
              </div>
            </DropdownMenuCheckboxItem>
          ))}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ==================== TASK CARD COMPONENT ====================
function TaskCard({ task, onComplete, onEdit, onDelete, isAdmin }) {
  const isOverdue = task.status !== 'completed' && new Date(task.due_date) < new Date();
  const priorityColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
  
  return (
    <Card className={`${isOverdue ? 'border-red-500/50' : ''} ${task.status === 'completed' ? 'opacity-75' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={priorityColor}>{task.priority}</Badge>
              <Badge variant="outline" className="capitalize">{task.task_type?.replace('_', ' ')}</Badge>
              {isOverdue && <Badge variant="destructive">Overdue</Badge>}
              {task.status === 'completed' && <Badge variant="default" className="bg-emerald-500">Completed</Badge>}
            </div>
            <h4 className="font-medium">{task.title}</h4>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                Due: {new Date(task.due_date).toLocaleDateString()}
              </span>
              {task.assigned_to_name && (
                <span className="flex items-center gap-1">
                  <UserCheck className="h-3 w-3" />
                  {task.assigned_to_name}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {task.status !== 'completed' && (
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => onComplete(task)}
                className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                title="Mark Complete"
              >
                <CheckCircle2 className="h-4 w-4" />
              </Button>
            )}
            {isAdmin && (
              <>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => onEdit(task)}
                  className="h-8 w-8 p-0"
                  title="Edit Task"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => onDelete(task)}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="Delete Task"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
        {task.progress !== undefined && task.progress > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span>Progress</span>
              <span>{task.progress}%</span>
            </div>
            <Progress value={task.progress} className="h-2" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== EDIT TASK DIALOG ====================
function EditTaskDialog({ open, onClose, task, auditors, allUsers, onTaskUpdated, tenantId }) {
  const [loading, setLoading] = useState(false);
  const [taskData, setTaskData] = useState({
    title: '',
    description: '',
    task_type: 'review_calls',
    priority: 'medium',
    assigned_to: '',
    due_date: '',
    related_user_id: '',
    status: 'pending'
  });

  useEffect(() => {
    if (open && task) {
      setTaskData({
        title: task.title || '',
        description: task.description || '',
        task_type: task.task_type || 'review_calls',
        priority: task.priority || 'medium',
        assigned_to: task.auditor_id || task.assigned_to || '',
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        related_user_id: task.related_user_id || '',
        status: task.status || 'pending'
      });
    }
  }, [open, task]);

  const handleSubmit = async () => {
    if (!taskData.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    if (!taskData.assigned_to) {
      toast.error('Please assign the task to an auditor');
      return;
    }

    setLoading(true);
    try {
      await updateQATask(task.id, {
        title: taskData.title,
        description: taskData.description,
        task_type: taskData.task_type,
        priority: taskData.priority,
        auditor_id: taskData.assigned_to,  // Map assigned_to to auditor_id
        due_date: taskData.due_date,
        related_user_id: taskData.related_user_id || null,
        status: taskData.status,
        tenant_id: tenantId
      });
      toast.success('Task updated successfully');
      onTaskUpdated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  const TASK_TYPES = [
    { value: 'review_calls', label: 'Review Calls' },
    { value: 'make_qa_notes', label: 'Make QA Notes' },
    { value: 'coaching_session', label: 'Coaching Session' },
    { value: 'review_homework', label: 'Review Homework' },
    { value: 'general', label: 'General' }
  ];

  const PRIORITIES = [
    { value: 'low', label: 'Low', color: 'bg-gray-500' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-500' },
    { value: 'high', label: 'High', color: 'bg-amber-500' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-500' }
  ];

  const STATUSES = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ];

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-blue-500" />
            Edit Task
          </DialogTitle>
          <DialogDescription>
            Update task details
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label>Task Title *</Label>
            <Input
              value={taskData.title}
              onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
              placeholder="e.g., Review critical calls from last week"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={taskData.description}
              onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
              placeholder="Detailed description..."
              rows={3}
            />
          </div>

          {/* Task Type, Priority, Status Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Task Type</Label>
              <Select
                value={taskData.task_type}
                onValueChange={(v) => setTaskData({ ...taskData, task_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={taskData.priority}
                onValueChange={(v) => setTaskData({ ...taskData, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${p.color}`} />
                        {p.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={taskData.status}
                onValueChange={(v) => setTaskData({ ...taskData, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assign To */}
          <div className="space-y-2">
            <Label>Assign To *</Label>
            <Select
              value={taskData.assigned_to}
              onValueChange={(v) => setTaskData({ ...taskData, assigned_to: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an auditor" />
              </SelectTrigger>
              <SelectContent>
                {auditors.map(auditor => (
                  <SelectItem key={auditor.id} value={auditor.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs bg-primary/10">
                          {auditor.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      {auditor.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={taskData.due_date}
              onChange={(e) => setTaskData({ ...taskData, due_date: e.target.value })}
            />
          </div>

          {/* Related User (Optional) */}
          <div className="space-y-2">
            <Label>Related User (Optional)</Label>
            <Select
              value={taskData.related_user_id || "none"}
              onValueChange={(v) => setTaskData({ ...taskData, related_user_id: v === "none" ? "" : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {allUsers.filter(u => u.role_id !== 'quality_auditor' && u.role_id !== 'lead_quality_auditor').slice(0, 50).map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== DELETE TASK DIALOG ====================
function DeleteTaskDialog({ open, onClose, task, onConfirm }) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onConfirm(task.id);
      toast.success('Task deleted successfully');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    } finally {
      setLoading(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            Delete Task
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this task?
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 bg-muted/50 rounded-lg space-y-2">
          <p className="font-medium">{task.title}</p>
          <p className="text-sm text-muted-foreground line-clamp-2">{task.description}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="capitalize">{task.task_type?.replace('_', ' ')}</Badge>
            <span>•</span>
            <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">
            This action cannot be undone. The task will be permanently deleted.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAIN COMPONENT ====================
export default function AuditorsDashboard() {
  const outletContext = useOutletContext() || {};
  const { tenant, user } = outletContext;
  const navigate = useNavigate();
  const tenantId = tenant?.id || tenant?.tenant_id;
  
  // User role checks
  const userRole = user?.role_id || user?.role;
  const isAdmin = userRole === 'admin' || userRole === 'owner' || user?.is_admin;
  const isAuditor = userRole === 'quality_auditor' || userRole === 'lead_quality_auditor' || isAdmin;

  // Main state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('performance');
  const [alertsSubTab, setAlertsSubTab] = useState('critical'); // Changed to match Alerts page tabs
  
  // Tab-specific loading states for better UX
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [callsLoading, setCallsLoading] = useState(false);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  
  // View mode for Calls tab: 'simplified' or 'complete'
  const [callsViewMode, setCallsViewMode] = useState('simplified');
  
  // Data state
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, reviewed: 0, total: 0 });
  const [alertCategoryStats, setAlertCategoryStats] = useState({
    critical: { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
    medium: { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
    improvement: { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
    non_compliance: { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 }
  });
  const [criticalCalls, setCriticalCalls] = useState([]);
  const [mediumCalls, setMediumCalls] = useState([]);
  const [improvementCalls, setImprovementCalls] = useState([]);
  const [nonComplianceCalls, setNonComplianceCalls] = useState([]);
  const [activities, setActivities] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  
  // Only Assigned Users toggle for Alerts tab
  const [showOnlyAssignedAlerts, setShowOnlyAssignedAlerts] = useState(true);
  
  // Only Assigned Users toggle for Calls tab
  const [showOnlyAssignedCalls, setShowOnlyAssignedCalls] = useState(true);
  
  // Calls statistics for summary cards
  const [callsStats, setCallsStats] = useState({
    total: 0,
    critical: 0,
    medium: 0,
    improvement: 0,
    good: 0,
    avgScore: 0,
    compliant: 0,
    nonCompliant: 0
  });
  
  // Users tab state - show all users with filter option
  const [usersTabSearchQuery, setUsersTabSearchQuery] = useState('');
  const [usersTabShowOnlyAssigned, setUsersTabShowOnlyAssigned] = useState(false);
  
  // Pagination state for Alerts tab
  const [alertsCurrentPage, setAlertsCurrentPage] = useState(1);
  const [alertsPageSize] = useState(100);
  const [alertsTotalPages, setAlertsTotalPages] = useState(1);
  
  // Calls state
  const [calls, setCalls] = useState([]);
  const [callsTotal, setCallsTotal] = useState(0);
  const [callsPage, setCallsPage] = useState(1);
  const [callsTotalPages, setCallsTotalPages] = useState(1);
  const [callsSearchQuery, setCallsSearchQuery] = useState('');
  const [callsScoreFilter, setCallsScoreFilter] = useState('all');
  const [callsComplianceFilter, setCallsComplianceFilter] = useState('all');
  const [callsAlertLevel, setCallsAlertLevel] = useState('all');
  const [callsClassification, setCallsClassification] = useState('all');
  const [callsCallType, setCallsCallType] = useState('all');
  const [callsSortField, setCallsSortField] = useState('call_date');
  const [callsSortOrder, setCallsSortOrder] = useState('desc');
  
  // Auditor selection (for admins)
  const [auditors, setAuditors] = useState([]);
  const [selectedAuditors, setSelectedAuditors] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [alertLevel, setAlertLevel] = useState('all');
  const [reviewStatus, setReviewStatus] = useState('unreviewed');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Selection state
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [bulkOverrideDialogOpen, setBulkOverrideDialogOpen] = useState(false);
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [deleteTaskDialogOpen, setDeleteTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Performance card drill-down dialog
  const [performanceDrillDown, setPerformanceDrillDown] = useState(null); // 'reviews', 'overrides', 'avgTime', 'compliance'
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  // ==================== DATA FETCHING ====================
  const fetchAuditors = useCallback(async () => {
    try {
      const res = await getUsers();
      const allUsersData = res.data || [];
      // Filter for auditor roles
      const auditorUsers = allUsersData.filter(u => 
        u.role_id === 'quality_auditor' || 
        u.role_id === 'lead_quality_auditor' ||
        u.role === 'quality_auditor' ||
        u.role === 'lead_quality_auditor'
      );
      setAuditors(auditorUsers);
      // Set ALL users for the users tab (not just auditors)
      setAllUsers(allUsersData);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  const fetchAlerts = useCallback(async (page = 1) => {
    setAlertsLoading(true);
    try {
      const baseParams = { page, page_size: alertsPageSize };
      if (tenantId) baseParams.tenant_id = tenantId;
      
      // Filter by assigned users if toggle is on and auditor has assigned users
      let assignedUserIds = [];
      if (showOnlyAssignedAlerts && assignedUsers.length > 0) {
        assignedUserIds = assignedUsers.map(u => u.user_id || u.id);
        baseParams.user_ids = assignedUserIds.join(',');
      }
      
      // First, get category stats (this gives us the REAL totals)
      // Pass the same user_ids filter to get accurate filtered stats
      const statsParams = { tenant_id: tenantId };
      if (showOnlyAssignedAlerts && assignedUsers.length > 0) {
        statsParams.user_ids = assignedUserIds.join(',');
      }
      const statsResp = await getAlertsCategoryStats(tenantId, statsParams).catch(() => ({ data: {} }));
      
      // Set category stats from API - these are the REAL totals
      if (statsResp.data) {
        setAlertCategoryStats({
          critical: statsResp.data.critical || { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
          medium: statsResp.data.medium || { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
          improvement: statsResp.data.improvement || { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 },
          non_compliance: statsResp.data.non_compliance || { total: 0, qa_reviewed: 0, qa_not_reviewed: 0 }
        });
      }
      
      // Fetch calls for current category with pagination
      let response;
      switch (alertsSubTab) {
        case 'critical':
          response = await getCalls({ ...baseParams, alert_level: 'critical' });
          setCriticalCalls(response.data?.calls || []);
          break;
        case 'medium':
          response = await getCalls({ ...baseParams, alert_level: 'medium' });
          setMediumCalls(response.data?.calls || []);
          break;
        case 'improvement':
          response = await getCalls({ ...baseParams, alert_level: 'improvement' });
          setImprovementCalls(response.data?.calls || []);
          break;
        case 'non-compliance':
          response = await getCalls({ ...baseParams, classification: 'high_alert' });
          setNonComplianceCalls(response.data?.calls || []);
          break;
        default:
          response = await getCalls({ ...baseParams, alert_level: 'critical' });
          setCriticalCalls(response.data?.calls || []);
      }
      
      // Update pagination info
      const total = response?.data?.total || 0;
      setAlertsTotalPages(Math.ceil(total / alertsPageSize) || 1);
      setAlertsCurrentPage(page);
      
      // Calculate summary
      const totals = statsResp.data || {};
      const totalAlerts = (totals.critical?.total || 0) + (totals.medium?.total || 0) + 
                          (totals.improvement?.total || 0) + (totals.non_compliance?.total || 0);
      const reviewedAlerts = (totals.critical?.qa_reviewed || 0) + (totals.medium?.qa_reviewed || 0) + 
                              (totals.improvement?.qa_reviewed || 0) + (totals.non_compliance?.qa_reviewed || 0);
      
      setSummary({
        pending: totalAlerts - reviewedAlerts,
        reviewed: reviewedAlerts,
        total: totalAlerts
      });
      
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setAlertsLoading(false);
    }
  }, [tenantId, showOnlyAssignedAlerts, assignedUsers, alertsSubTab, alertsPageSize]);
  
  // Handle alerts tab change
  const handleAlertsSubTabChange = (newTab) => {
    setAlertsSubTab(newTab);
    setAlertsCurrentPage(1);
    setSelectedAlerts([]);
    fetchAlerts(1);
  };
  
  // Handle alerts page change
  const handleAlertsPageChange = (newPage) => {
    fetchAlerts(newPage);
  };

  const fetchActivities = useCallback(async () => {
    try {
      const auditorFilter = isAdmin && selectedAuditors.length > 0 && selectedAuditors.length < auditors.length
        ? selectedAuditors.join(',')
        : null;
      const activityParams = {};
      if (tenantId) activityParams.tenant_id = tenantId;
      if (auditorFilter) activityParams.auditor_id = auditorFilter;
      if (dateFilter !== 'all') activityParams.date_filter = dateFilter;
      
      const res = await getQAActivities(activityParams);
      setActivities(res.data?.activities || res.data || []);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      setActivities([]);
    }
  }, [tenantId, dateFilter, isAdmin, selectedAuditors, auditors.length]);

  const fetchPerformance = useCallback(async () => {
    setPerformanceLoading(true);
    try {
      const auditorFilter = isAdmin && selectedAuditors.length > 0 && selectedAuditors.length < auditors.length
        ? selectedAuditors[0]  // getAuditorPerformanceReport accepts single auditor_id
        : null;
      const period = dateFilter === 'today' ? '7d' : dateFilter === 'week' ? '7d' : dateFilter === 'month' ? '30d' : '30d';
      const res = await getAuditorPerformanceReport(period, auditorFilter);
      setPerformance(res.data || null);
    } catch (error) {
      console.error('Failed to fetch performance:', error);
      setPerformance(null);
    } finally {
      setPerformanceLoading(false);
    }
  }, [tenantId, dateFilter, isAdmin, selectedAuditors, auditors.length]);

  const fetchTasks = useCallback(async () => {
    if (!tenantId) {
      console.warn('fetchTasks: No tenant_id available');
      setTasks([]);
      return;
    }
    try {
      const auditorFilter = isAdmin && selectedAuditors.length === 1 ? selectedAuditors[0] : user?.id;
      const res = await getQATasks(tenantId, auditorFilter);
      // Handle different response formats
      const tasksData = res.data?.tasks || res.data || [];
      setTasks(Array.isArray(tasksData) ? tasksData : []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      setTasks([]);
    }
  }, [tenantId, isAdmin, selectedAuditors, user?.id]);

  const fetchAssignedUsers = useCallback(async () => {
    if (!tenantId || !user?.id) {
      console.warn('fetchAssignedUsers: Missing tenant_id or user_id');
      setAssignedUsers([]);
      return;
    }
    try {
      const res = await getMyAssignedUsers(tenantId, user?.id, dateFilter !== 'all' ? dateFilter : undefined);
      const usersData = res.data?.users || res.data || [];
      setAssignedUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error('Failed to fetch assigned users:', error);
      setAssignedUsers([]);
    }
  }, [tenantId, user?.id, dateFilter]);

  const fetchCalls = useCallback(async () => {
    if (!tenantId) {
      console.warn('fetchCalls: No tenant_id available');
      setCalls([]);
      return;
    }
    setCallsLoading(true);
    try {
      // Build params
      const params = {
        tenant_id: tenantId,
        page: callsPage,
        page_size: pageSize,
        sort_by: callsSortField,
        sort_order: callsSortOrder
      };
      
      // Filter by assigned users when toggle is ON
      if (showOnlyAssignedCalls && assignedUsers.length > 0) {
        params.user_ids = assignedUsers.map(u => u.id || u.user_id).join(',');
      }
      
      // For admins with selected auditors, get their assigned users
      if (isAdmin && selectedAuditors.length > 0 && selectedAuditors.length < auditors.length) {
        params.auditor_ids = selectedAuditors.join(',');
      }
      
      if (callsSearchQuery) params.search = callsSearchQuery;
      if (callsScoreFilter !== 'all') {
        if (callsScoreFilter === 'high') params.min_score = 80;
        else if (callsScoreFilter === 'medium') { params.min_score = 60; params.max_score = 79; }
        else if (callsScoreFilter === 'low') params.max_score = 59;
      }
      if (callsComplianceFilter !== 'all') {
        params.compliance_status = callsComplianceFilter;
      }
      if (callsAlertLevel !== 'all') {
        params.alert_level = callsAlertLevel;
      }
      if (callsClassification !== 'all') {
        params.classification = callsClassification;
      }
      if (callsCallType !== 'all') {
        params.call_type = callsCallType;
      }
      if (dateFilter !== 'all') params.date_filter = dateFilter;
      
      const res = await getCalls(params);
      const callsData = res.data?.calls || res.data || [];
      setCalls(Array.isArray(callsData) ? callsData : []);
      setCallsTotal(res.data?.total || callsData.length);
      setCallsTotalPages(Math.ceil((res.data?.total || callsData.length) / pageSize));
      
      // Calculate stats from the calls for summary cards
      // Fetch all calls (without pagination) for accurate stats OR use separate stats endpoint
      const statsParams = { ...params };
      delete statsParams.page;
      delete statsParams.page_size;
      statsParams.limit = 1000; // Get more for stats
      
      try {
        const statsRes = await getCalls(statsParams);
        const allCallsData = statsRes.data?.calls || statsRes.data || [];
        
        const stats = {
          total: allCallsData.length,
          critical: allCallsData.filter(c => c.alert_level === 'critical').length,
          medium: allCallsData.filter(c => c.alert_level === 'medium').length,
          improvement: allCallsData.filter(c => c.alert_level === 'improvement' || c.classification === 'improvement').length,
          good: allCallsData.filter(c => c.alert_level === 'good' || c.classification === 'good' || c.classification === 'best').length,
          compliant: allCallsData.filter(c => !c.compliance_alerts || c.compliance_alerts.length === 0).length,
          nonCompliant: allCallsData.filter(c => c.compliance_alerts && c.compliance_alerts.length > 0).length,
          avgScore: 0
        };
        
        // Calculate average score
        const scoresArray = allCallsData.filter(c => c.average_score != null).map(c => c.average_score);
        if (scoresArray.length > 0) {
          stats.avgScore = Math.round(scoresArray.reduce((a, b) => a + b, 0) / scoresArray.length * 10) / 10;
        }
        
        setCallsStats(stats);
      } catch (statsError) {
        console.error('Failed to fetch calls stats:', statsError);
      }
      
    } catch (error) {
      console.error('Failed to fetch calls:', error);
      setCalls([]);
    } finally {
      setCallsLoading(false);
    }
  }, [tenantId, callsPage, callsSearchQuery, callsScoreFilter, callsComplianceFilter, callsAlertLevel, callsClassification, callsCallType, callsSortField, callsSortOrder, dateFilter, isAdmin, selectedAuditors, auditors.length, assignedUsers, showOnlyAssignedCalls]);

  // Initial load - wait for tenant context
  useEffect(() => {
    if (!tenantId) {
      return; // Wait for tenant context
    }
    
    const loadData = async () => {
      setLoading(true);
      await fetchAuditors();
      // Fetch assigned users FIRST (required for "Only Assigned Users" filter to work)
      await fetchAssignedUsers();
      // Now fetch data that depends on assignedUsers
      await Promise.all([
        fetchAlerts(),
        fetchActivities(),
        fetchPerformance(),
        fetchTasks(),
        fetchCalls()
      ]);
      setLoading(false);
    };
    loadData();
  }, [tenantId]);

  // Refetch when filters change (including assignedUsers for toggle to work properly)
  useEffect(() => {
    if (!loading) {
      fetchAlerts();
    }
  }, [reviewStatus, alertLevel, currentPage, searchQuery, dateFilter, selectedAuditors, showOnlyAssignedAlerts, assignedUsers]);

  useEffect(() => {
    if (!loading) {
      fetchActivities();
      fetchPerformance();
      fetchTasks();
    }
  }, [dateFilter, selectedAuditors]);

  // Refetch calls when filters change
  useEffect(() => {
    if (!loading) {
      fetchCalls();
    }
  }, [callsPage, callsSearchQuery, callsScoreFilter, callsComplianceFilter, callsAlertLevel, callsClassification, callsCallType, callsSortField, callsSortOrder, dateFilter, selectedAuditors, showOnlyAssignedCalls, assignedUsers]);

  // Clear selection when tab/filter changes
  useEffect(() => {
    setSelectedAlerts([]);
    setCurrentPage(1);
  }, [alertsSubTab, reviewStatus, selectedAuditors]);

  // ==================== HANDLERS ====================
  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      fetchAlerts(),
      fetchActivities(),
      fetchPerformance(),
      fetchTasks(),
      fetchCalls()
    ]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAlerts(filteredAlerts.map(a => a.id));
    } else {
      setSelectedAlerts([]);
    }
  };

  const handleSelectAlert = (alertId, checked) => {
    if (checked) {
      setSelectedAlerts(prev => [...prev, alertId]);
    } else {
      setSelectedAlerts(prev => prev.filter(id => id !== alertId));
    }
  };

  const handleReviewAlert = async (alertId, notes) => {
    try {
      await reviewAlert(alertId, { qa_notes: notes });
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to review alert');
    }
  };

  const handleOverrideCompliance = async (alertId, data) => {
    try {
      await overrideAlertCompliance(alertId, data);
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to override compliance');
    }
  };

  // Quick QA Review handler for alerts tab
  const handleQuickQAReview = async (call) => {
    try {
      await qaReviewCall(call.id, 'Reviewed from Auditors Dashboard', false);
      toast.success('Call marked as QA reviewed');
      fetchAlerts();
    } catch (error) {
      console.error('QA review failed:', error);
      toast.error('Failed to review call');
    }
  };

  const handleBulkReview = async () => {
    if (selectedAlerts.length === 0) return;
    setBulkActionLoading(true);
    try {
      await bulkReviewAlerts(selectedAlerts);
      toast.success(`${selectedAlerts.length} alerts marked as reviewed`);
      setSelectedAlerts([]);
      fetchAlerts();
    } catch (error) {
      toast.error('Bulk review failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkOverride = async (overrideData) => {
    if (selectedAlerts.length === 0) return;
    const res = await bulkOverrideAlerts(
      selectedAlerts,
      overrideData.new_compliance_status,
      overrideData.override_reason,
      overrideData.adjust_score,
      overrideData.qa_notes
    );
    toast.success(`${res.data.overridden_count} alerts overridden`);
    setSelectedAlerts([]);
    fetchAlerts();
  };

  const handleQuickMarkCompliant = async (alertId) => {
    try {
      await overrideAlertCompliance(alertId, {
        new_compliance_status: true,
        override_reason: 'Quick mark compliant by auditor',
        adjust_score: true,
        qa_notes: null
      });
      toast.success('Alert marked as compliant');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to mark compliant');
    }
  };

  const handleCompleteTask = async (task) => {
    try {
      await completeQATask(task.id, { completed_notes: 'Completed via dashboard' });
      toast.success('Task completed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setEditTaskDialogOpen(true);
  };

  const handleDeleteTask = (task) => {
    setSelectedTask(task);
    setDeleteTaskDialogOpen(true);
  };

  const handleConfirmDeleteTask = async (taskId) => {
    await deleteQATask(taskId);
    fetchTasks();
  };

  // ==================== COMPUTED VALUES ====================
  const pendingTasks = (tasks || []).filter(t => t.status === 'pending' || t.status === 'in_progress');
  const completedTasks = (tasks || []).filter(t => t.status === 'completed');

  // Get current alerts based on selected sub-tab
  const getCurrentAlerts = () => {
    switch (alertsSubTab) {
      case 'critical':
        return criticalCalls;
      case 'medium':
        return mediumCalls;
      case 'improvement':
        return improvementCalls;
      case 'non-compliance':
        return nonComplianceCalls;
      default:
        return criticalCalls;
    }
  };

  // Filter alerts based on search, QA status, etc.
  const getFilteredAlerts = () => {
    let calls = getCurrentAlerts();
    
    // Apply search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      calls = calls.filter(call => 
        call.user?.name?.toLowerCase().includes(search) ||
        call.phone_number?.toLowerCase().includes(search) ||
        call.filename?.toLowerCase().includes(search)
      );
    }
    
    // Apply review status filter
    if (reviewStatus === 'unreviewed') {
      calls = calls.filter(call => !call.qa_reviewed);
    } else if (reviewStatus === 'reviewed') {
      calls = calls.filter(call => call.qa_reviewed);
    }
    
    // Apply alert level filter (for non-compliance tab which can have mixed levels)
    if (alertLevel !== 'all') {
      calls = calls.filter(call => call.alert_level === alertLevel);
    }
    
    return calls;
  };

  const filteredAlerts = getFilteredAlerts();

  // ==================== RENDER ====================
  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="auditors-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Auditors Dashboard</h1>
          <p className="text-muted-foreground">
            {isAdmin ? 'Manage and monitor all auditor activities' : 'Review alerts, manage tasks, and track performance'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Auditor Selector for Admins */}
          <AuditorSelector
            auditors={auditors}
            selectedAuditors={selectedAuditors}
            onSelectionChange={setSelectedAuditors}
            isAdmin={isAdmin}
          />
          
          {/* Date Filter */}
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-[140px]">
              <CalendarDays className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card 
          className="bg-amber-500/10 border-amber-500/30 cursor-pointer hover:bg-amber-500/20 transition-colors"
          onClick={() => { setActiveTab('alerts'); setReviewStatus('unreviewed'); }}
          data-testid="card-pending-review"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Pending Review</p>
                <p className="text-3xl font-bold text-amber-700 dark:text-amber-300">{summary.pending}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-emerald-500/10 border-emerald-500/30 cursor-pointer hover:bg-emerald-500/20 transition-colors"
          onClick={() => { setActiveTab('alerts'); setReviewStatus('reviewed'); }}
          data-testid="card-reviewed"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">Reviewed</p>
                <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">{summary.reviewed}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-indigo-500/10 border-indigo-500/30 cursor-pointer hover:bg-indigo-500/20 transition-colors"
          onClick={() => setActiveTab('calls')}
          data-testid="card-total-calls"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">Total Calls</p>
                <p className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">{callsTotal}</p>
              </div>
              <PhoneCall className="h-8 w-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-blue-500/10 border-blue-500/30 cursor-pointer hover:bg-blue-500/20 transition-colors"
          onClick={() => setActiveTab('tasks')}
          data-testid="card-active-tasks"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Active Tasks</p>
                <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{pendingTasks.length}</p>
              </div>
              <ClipboardList className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-purple-500/10 border-purple-500/30 cursor-pointer hover:bg-purple-500/20 transition-colors"
          onClick={() => setActiveTab('users')}
          data-testid="card-assigned-users"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">All Users</p>
                <p className="text-3xl font-bold text-purple-700 dark:text-purple-300">{allUsers.length}</p>
              </div>
              <Users className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Focus - Quick Priority Guide */}
      <Card className="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-900 dark:to-blue-900/30 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-800 dark:text-blue-200">Today's Focus</span>
              </div>
              
              {alertCategoryStats.critical.qa_not_reviewed > 0 && (
                <div className="flex items-center gap-2 px-3 py-1 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <AlertOctagon className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-700 dark:text-red-300">
                    {alertCategoryStats.critical.qa_not_reviewed} critical alerts need review
                  </span>
                </div>
              )}
              
              {pendingTasks.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                  <ClipboardList className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    {pendingTasks.length} tasks pending
                  </span>
                </div>
              )}
              
              {alertCategoryStats.critical.qa_not_reviewed === 0 && pendingTasks.length === 0 && (
                <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    All caught up!
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {alertCategoryStats.critical.qa_not_reviewed > 0 && (
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => { setActiveTab('alerts'); handleAlertsSubTabChange('critical'); }}
                >
                  Review Critical
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="performance" className="gap-2" data-testid="tab-performance">
            <BarChart3 className="h-4 w-4" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="calls" className="gap-2" data-testid="tab-calls">
            <PhoneCall className="h-4 w-4" />
            Calls
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2" data-testid="tab-alerts">
            <AlertTriangle className="h-4 w-4" />
            Alerts
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-2" data-testid="tab-tasks">
            <ClipboardList className="h-4 w-4" />
            Tasks
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
            <Users className="h-4 w-4" />
            All Users
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-2" data-testid="tab-activity">
            <History className="h-4 w-4" />
            Activity Log
          </TabsTrigger>
        </TabsList>

        {/* ==================== ALERTS TAB ==================== */}
        <TabsContent value="alerts" className="space-y-4">
          {/* Alert Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card 
              className={`cursor-pointer transition-all ${alertsSubTab === 'critical' ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20' : 'hover:bg-muted/50'}`}
              onClick={() => handleAlertsSubTabChange('critical')}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Critical Alerts</p>
                    <p className="text-3xl font-bold text-red-700">{alertCategoryStats.critical.total}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {alertCategoryStats.critical.qa_reviewed} reviewed • {alertCategoryStats.critical.qa_not_reviewed} pending
                    </p>
                  </div>
                  <AlertOctagon className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all ${alertsSubTab === 'medium' ? 'ring-2 ring-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'hover:bg-muted/50'}`}
              onClick={() => handleAlertsSubTabChange('medium')}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600 font-medium">Medium Alerts</p>
                    <p className="text-3xl font-bold text-amber-700">{alertCategoryStats.medium.total}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {alertCategoryStats.medium.qa_reviewed} reviewed • {alertCategoryStats.medium.qa_not_reviewed} pending
                    </p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all ${alertsSubTab === 'improvement' ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-muted/50'}`}
              onClick={() => handleAlertsSubTabChange('improvement')}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Improvement Needed</p>
                    <p className="text-3xl font-bold text-blue-700">{alertCategoryStats.improvement.total}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {alertCategoryStats.improvement.qa_reviewed} reviewed • {alertCategoryStats.improvement.qa_not_reviewed} pending
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all ${alertsSubTab === 'non-compliance' ? 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'hover:bg-muted/50'}`}
              onClick={() => handleAlertsSubTabChange('non-compliance')}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Non-Compliance</p>
                    <p className="text-3xl font-bold text-purple-700">{alertCategoryStats.non_compliance.total}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {alertCategoryStats.non_compliance.qa_reviewed} reviewed • {alertCategoryStats.non_compliance.qa_not_reviewed} pending
                    </p>
                  </div>
                  <ShieldX className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters Row */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by user, phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={reviewStatus} onValueChange={setReviewStatus}>
                  <SelectTrigger className="w-[170px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unreviewed">QA Not Reviewed</SelectItem>
                    <SelectItem value="reviewed">QA Reviewed</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={alertLevel} onValueChange={setAlertLevel}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Alert Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>

                {/* Only Assigned Users Toggle */}
                <div className="flex items-center gap-2 ml-auto">
                  <Switch
                    id="only-assigned"
                    checked={showOnlyAssignedAlerts}
                    onCheckedChange={setShowOnlyAssignedAlerts}
                  />
                  <Label htmlFor="only-assigned" className="text-sm cursor-pointer">
                    Only Assigned Users
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Banner: Viewing All Users (when toggle is OFF) */}
          {!showOnlyAssignedAlerts && assignedUsers.length > 0 && (
            <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg" data-testid="alerts-all-users-banner">
              <Eye className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Viewing All Users
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  You're viewing alerts from all users. Enable "Only Assigned Users" to filter to your assigned team members ({assignedUsers.length} assigned).
                </p>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setShowOnlyAssignedAlerts(true)}
                className="text-amber-700 border-amber-300 hover:bg-amber-100 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-900/40"
              >
                Show Only Assigned
              </Button>
            </div>
          )}

          {/* Bulk Actions Toolbar */}
          {selectedAlerts.length > 0 && (
            <div className="flex items-center justify-between gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedAlerts.length === filteredAlerts.length && filteredAlerts.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  {selectedAlerts.length} alert{selectedAlerts.length > 1 ? 's' : ''} selected
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleBulkReview}
                  disabled={bulkActionLoading}
                  className="gap-2"
                >
                  {bulkActionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Mark Reviewed
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="default" className="gap-2">
                      <Edit className="h-4 w-4" />
                      Override
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setBulkOverrideDialogOpen(true)} className="gap-2">
                      <ThumbsUp className="h-4 w-4 text-green-600" />
                      Mark as Compliant
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setBulkOverrideDialogOpen(true)} className="gap-2">
                      <ThumbsDown className="h-4 w-4 text-red-600" />
                      Mark as Non-Compliant
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setBulkOverrideDialogOpen(true)} className="gap-2">
                      <Edit className="h-4 w-4" />
                      Custom Override...
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setSelectedAlerts([])}
                  className="text-muted-foreground"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* Alerts Table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {alertsSubTab === 'critical' && <AlertOctagon className="h-5 w-5 text-red-500" />}
                    {alertsSubTab === 'medium' && <AlertTriangle className="h-5 w-5 text-amber-500" />}
                    {alertsSubTab === 'improvement' && <TrendingUp className="h-5 w-5 text-blue-500" />}
                    {alertsSubTab === 'non-compliance' && <ShieldX className="h-5 w-5 text-purple-500" />}
                    {alertsSubTab === 'critical' ? 'Critical Calls' : 
                     alertsSubTab === 'medium' ? 'Medium Priority' : 
                     alertsSubTab === 'improvement' ? 'Improvement Needed' : 'Non-Compliance'}
                  </CardTitle>
                  <CardDescription>
                    {filteredAlerts.length} calls {reviewStatus !== 'all' ? `(${reviewStatus})` : ''}
                    {showOnlyAssignedAlerts && assignedUsers.length > 0 ? ' • Filtered to assigned users only' : ''}
                  </CardDescription>
                </div>
                {alertsLoading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent>
              {alertsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredAlerts.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedAlerts.length === filteredAlerts.length && filteredAlerts.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Score</TableHead>
                        <TableHead>Alert Level</TableHead>
                        <TableHead>Classification</TableHead>
                        <TableHead>QA Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAlerts.map((call) => {
                        const isCompliant = call.has_notes || call.manually_marked_compliant;
                        return (
                          <TableRow 
                            key={call.id}
                            className={selectedAlerts.includes(call.id) ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedAlerts.includes(call.id)}
                                onCheckedChange={(checked) => handleSelectAlert(call.id, checked)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="bg-primary/10 text-xs">
                                    {call.user?.name?.charAt(0) || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-sm">{call.user?.name || 'Unknown'}</p>
                                  <p className="text-xs text-muted-foreground">{call.phone_number}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {new Date(call.call_date).toLocaleDateString()}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={`font-medium ${
                                (call.average_score || 0) >= 80 ? 'text-green-600' :
                                (call.average_score || 0) >= 60 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {call.average_score?.toFixed(1) || '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                call.alert_level === 'critical' ? 'destructive' : 
                                call.alert_level === 'medium' ? 'secondary' : 'outline'
                              } className="capitalize">
                                {call.alert_level || 'improvement'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {call.classification?.replace('_', ' ') || '-'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {call.qa_reviewed ? (
                                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Reviewed
                                </Badge>
                              ) : (
                                <Badge variant="secondary">
                                  Pending Review
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                {!call.qa_reviewed && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleQuickQAReview(call)}
                                    className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    title="Quick QA Review"
                                  >
                                    <ClipboardCheck className="h-4 w-4" />
                                  </Button>
                                )}
                                
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 px-2">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => { setSelectedAlert(call); setReviewDialogOpen(true); }}>
                                      <Eye className="h-4 w-4 mr-2" />
                                      QA Review / Override
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => window.open(`/dashboard/call/${call.id}?from=alerts`, '_blank')}>
                                      <Phone className="h-4 w-4 mr-2" />
                                      View Call (New Tab)
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(`/dashboard/call/${call.id}?from=alerts`, '_blank')}
                                  className="h-8 px-2"
                                  title="Open in new tab"
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination Controls */}
                  {alertsTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <span className="text-sm text-muted-foreground">
                        Page {alertsCurrentPage} of {alertsTotalPages}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAlertsPageChange(alertsCurrentPage - 1)}
                          disabled={alertsCurrentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <div className="flex items-center gap-1">
                          {[...Array(Math.min(5, alertsTotalPages))].map((_, i) => {
                            let pageNum;
                            if (alertsTotalPages <= 5) {
                              pageNum = i + 1;
                            } else if (alertsCurrentPage <= 3) {
                              pageNum = i + 1;
                            } else if (alertsCurrentPage >= alertsTotalPages - 2) {
                              pageNum = alertsTotalPages - 4 + i;
                            } else {
                              pageNum = alertsCurrentPage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={pageNum === alertsCurrentPage ? "default" : "outline"}
                                size="sm"
                                className="w-9"
                                onClick={() => handleAlertsPageChange(pageNum)}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAlertsPageChange(alertsCurrentPage + 1)}
                          disabled={alertsCurrentPage === alertsTotalPages}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-green-600">All Clear!</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {showOnlyAssignedAlerts 
                      ? 'No alerts for your assigned users in this category'
                      : 'No alerts in this category'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== CALLS TAB ==================== */}
        <TabsContent value="calls" className="space-y-4" data-testid="calls-tab-content">
          {/* Summary Cards */}
          <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
            <Card 
              className={cn(
                "cursor-pointer hover:shadow-md transition-shadow",
                callsAlertLevel === 'all' && "ring-2 ring-primary"
              )}
              onClick={() => { setCallsAlertLevel('all'); setCallsClassification('all'); }}
              data-testid="calls-card-total"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Calls</p>
                    <p className="text-2xl font-bold">{callsStats.total}</p>
                  </div>
                  <Phone className="h-8 w-8 text-primary/50" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={cn(
                "cursor-pointer hover:shadow-md transition-shadow bg-red-500/10 border-red-500/30",
                callsAlertLevel === 'critical' && "ring-2 ring-red-500"
              )}
              onClick={() => { setCallsAlertLevel('critical'); setCallsClassification('all'); }}
              data-testid="calls-card-critical"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 dark:text-red-400">Critical</p>
                    <p className="text-2xl font-bold text-red-700 dark:text-red-300">{callsStats.critical}</p>
                  </div>
                  <AlertOctagon className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={cn(
                "cursor-pointer hover:shadow-md transition-shadow bg-amber-500/10 border-amber-500/30",
                callsAlertLevel === 'medium' && "ring-2 ring-amber-500"
              )}
              onClick={() => { setCallsAlertLevel('medium'); setCallsClassification('all'); }}
              data-testid="calls-card-medium"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600 dark:text-amber-400">Medium</p>
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{callsStats.medium}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={cn(
                "cursor-pointer hover:shadow-md transition-shadow bg-blue-500/10 border-blue-500/30",
                callsAlertLevel === 'improvement' && "ring-2 ring-blue-500"
              )}
              onClick={() => { setCallsAlertLevel('improvement'); setCallsClassification('all'); }}
              data-testid="calls-card-improvement"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 dark:text-blue-400">Improvement</p>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{callsStats.improvement}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={cn(
                "cursor-pointer hover:shadow-md transition-shadow bg-green-500/10 border-green-500/30",
                callsAlertLevel === 'good' && "ring-2 ring-green-500"
              )}
              onClick={() => { setCallsAlertLevel('good'); setCallsClassification('all'); }}
              data-testid="calls-card-good"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 dark:text-green-400">Good</p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-300">{callsStats.good}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="calls-card-avg-score">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Score</p>
                    <p className="text-2xl font-bold">{callsStats.avgScore || 'N/A'}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-primary/50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters & Toggle Row */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Left side - Toggle and View Mode */}
                <div className="flex items-center gap-4">
                  {/* Only Assigned Users Toggle - Always show for auditors */}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                    <Switch
                      id="show-assigned-calls"
                      checked={showOnlyAssignedCalls}
                      onCheckedChange={setShowOnlyAssignedCalls}
                      data-testid="calls-assigned-toggle"
                    />
                    <Label htmlFor="show-assigned-calls" className="text-sm cursor-pointer whitespace-nowrap">
                      Only Assigned Users
                    </Label>
                    {showOnlyAssignedCalls && assignedUsers.length === 0 && (
                      <span className="text-xs text-muted-foreground ml-1">(No users assigned)</span>
                    )}
                  </div>
                  
                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground mr-2">View:</span>
                    <Button
                      variant={callsViewMode === 'simplified' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCallsViewMode('simplified')}
                      className="gap-2"
                      data-testid="calls-simplified-view"
                    >
                      <ListFilter className="h-4 w-4" />
                      Simplified
                    </Button>
                    <Button
                      variant={callsViewMode === 'complete' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCallsViewMode('complete')}
                      className="gap-2"
                      data-testid="calls-complete-view"
                    >
                      <Filter className="h-4 w-4" />
                      Complete
                    </Button>
                  </div>
                </div>
                
                {/* Common Filters */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search calls..."
                      value={callsSearchQuery}
                      onChange={(e) => setCallsSearchQuery(e.target.value)}
                      className="pl-10 w-[200px]"
                      data-testid="calls-search"
                    />
                  </div>
                  
                  <Select value={callsScoreFilter} onValueChange={setCallsScoreFilter}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Score" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Scores</SelectItem>
                      <SelectItem value="high">High (80+)</SelectItem>
                      <SelectItem value="medium">Medium (60-79)</SelectItem>
                      <SelectItem value="low">Low (&lt;60)</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={callsComplianceFilter} onValueChange={setCallsComplianceFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Compliance" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="compliant">Compliant</SelectItem>
                      <SelectItem value="non_compliant">Non-Compliant</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Complete View Extra Filters */}
                  {callsViewMode === 'complete' && (
                    <>
                      <Select value={callsAlertLevel} onValueChange={setCallsAlertLevel}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Alert Level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Levels</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                          <SelectItem value="high_alert">High Alert</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="improvement">Improvement</SelectItem>
                          <SelectItem value="good">Good</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={callsClassification} onValueChange={setCallsClassification}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Classification" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Classifications</SelectItem>
                          <SelectItem value="best">Best</SelectItem>
                          <SelectItem value="good">Good</SelectItem>
                          <SelectItem value="improvement">Improvement</SelectItem>
                          <SelectItem value="high_alert">High Alert</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={callsCallType} onValueChange={setCallsCallType}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Call Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="introduction">Introduction</SelectItem>
                          <SelectItem value="screening">Screening</SelectItem>
                          <SelectItem value="interview">Interview</SelectItem>
                          <SelectItem value="followup">Follow-up</SelectItem>
                          <SelectItem value="client">Client</SelectItem>
                          <SelectItem value="voicemail">Voicemail</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Banner: Viewing All Users (when toggle is OFF in Calls tab) */}
          {!showOnlyAssignedCalls && assignedUsers.length > 0 && (
            <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg" data-testid="calls-all-users-banner">
              <Eye className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Viewing All Users' Calls
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  You're viewing calls from all users. Enable "Only Assigned Users" to focus on your assigned team ({assignedUsers.length} assigned).
                </p>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setShowOnlyAssignedCalls(true)}
                className="text-amber-700 border-amber-300 hover:bg-amber-100 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-900/40"
              >
                Show Only Assigned
              </Button>
            </div>
          )}

          {/* Calls Table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    {callsViewMode === 'simplified' ? 'Calls Overview' : 'Calls - Full Details'}
                  </CardTitle>
                  <CardDescription>
                    {callsTotal} calls found • Page {callsPage} of {callsTotalPages}
                  </CardDescription>
                </div>
                {callsViewMode === 'complete' && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Sort by:</span>
                    <Select 
                      value={`${callsSortField}-${callsSortOrder}`} 
                      onValueChange={(v) => {
                        const [field, order] = v.split('-');
                        setCallsSortField(field);
                        setCallsSortOrder(order);
                      }}
                    >
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="call_date-desc">Date (Newest)</SelectItem>
                        <SelectItem value="call_date-asc">Date (Oldest)</SelectItem>
                        <SelectItem value="average_score-desc">Score (High-Low)</SelectItem>
                        <SelectItem value="average_score-asc">Score (Low-High)</SelectItem>
                        <SelectItem value="duration-desc">Duration (Long-Short)</SelectItem>
                        <SelectItem value="duration-asc">Duration (Short-Long)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {callsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : calls.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Call Type</TableHead>
                        <TableHead>Score</TableHead>
                        {callsViewMode === 'complete' && (
                          <>
                            <TableHead>Alert</TableHead>
                            <TableHead>Classification</TableHead>
                          </>
                        )}
                        <TableHead>Compliance</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {calls.map((call) => {
                        const score = call.average_score || call.quality_score || 0;
                        const isCompliant = call.compliance_status === 'compliant' || call.has_notes;
                        const scoreColor = score >= 80 ? 'text-emerald-600' : score >= 60 ? 'text-amber-600' : 'text-red-600';
                        const alertLevel = call.alert_level || 'good';
                        const classification = call.classification || 'good';
                        
                        return (
                          <TableRow key={call.id} data-testid={`call-row-${call.id}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="bg-primary/10 text-xs">
                                    {call.user_name?.charAt(0) || call.recruiter_name?.charAt(0) || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-sm">{call.user_name || call.recruiter_name || 'Unknown'}</p>
                                  <p className="text-xs text-muted-foreground">{call.candidate_phone || call.phone_number}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {new Date(call.call_date || call.created_at).toLocaleDateString()}
                              </span>
                              <p className="text-xs text-muted-foreground">
                                {new Date(call.call_date || call.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {call.call_type?.replace(/_/g, ' ') || 'Unknown'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className={`font-semibold ${scoreColor}`}>
                                {score > 0 ? score.toFixed(1) : '-'}
                              </span>
                            </TableCell>
                            {callsViewMode === 'complete' && (
                              <>
                                <TableCell>
                                  <Badge 
                                    variant={alertLevel === 'critical' ? 'destructive' : alertLevel === 'high_alert' ? 'destructive' : 'secondary'}
                                    className="capitalize"
                                  >
                                    {alertLevel?.replace(/_/g, ' ')}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge 
                                    variant="outline"
                                    className={`capitalize ${
                                      classification === 'best' ? 'border-emerald-500 text-emerald-600' :
                                      classification === 'good' ? 'border-blue-500 text-blue-600' :
                                      classification === 'improvement' ? 'border-amber-500 text-amber-600' :
                                      'border-red-500 text-red-600'
                                    }`}
                                  >
                                    {classification?.replace(/_/g, ' ')}
                                  </Badge>
                                </TableCell>
                              </>
                            )}
                            <TableCell>
                              <Badge variant={isCompliant ? 'default' : 'destructive'}>
                                {isCompliant ? 'Compliant' : 'Non-Compliant'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const callId = call.id || call._id;
                                  if (callId) navigate(`/dashboard/call/${callId}`);
                                }}
                                className="h-8 px-2"
                                disabled={!call.id && !call._id}
                                data-testid={`view-call-${call.id}`}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {callsTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Showing {((callsPage - 1) * pageSize) + 1} to {Math.min(callsPage * pageSize, callsTotal)} of {callsTotal}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCallsPage(p => Math.max(1, p - 1))}
                          disabled={callsPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm">Page {callsPage} of {callsTotalPages}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCallsPage(p => Math.min(callsTotalPages, p + 1))}
                          disabled={callsPage === callsTotalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <Phone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="font-medium">No Calls Found</p>
                  <p className="text-sm text-muted-foreground">
                    {!isAdmin && assignedUsers.length === 0 
                      ? 'No users assigned to you yet' 
                      : 'No calls match your current filters'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== TASKS TAB ==================== */}
        <TabsContent value="tasks" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">QA Tasks</h3>
            {isAdmin && (
              <Button className="gap-2" onClick={() => setCreateTaskDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Create Task
              </Button>
            )}
          </div>

          <Tabs defaultValue="pending" className="w-full">
            <TabsList>
              <TabsTrigger value="pending" className="gap-2">
                <Clock className="h-4 w-4" />
                Pending ({pendingTasks.length})
              </TabsTrigger>
              <TabsTrigger value="completed" className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Completed ({completedTasks.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="mt-4">
              {pendingTasks.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {pendingTasks.map(task => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      isAdmin={isAdmin}
                      onComplete={handleCompleteTask}
                      onEdit={handleEditTask}
                      onDelete={handleDeleteTask}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-12">
                    <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                    <p className="font-medium">No Pending Tasks</p>
                    <p className="text-sm text-muted-foreground">All tasks are completed</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="completed" className="mt-4">
              {completedTasks.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {completedTasks.map(task => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      isAdmin={isAdmin}
                      onComplete={() => {}}
                      onEdit={handleEditTask}
                      onDelete={handleDeleteTask}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-12">
                    <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="font-medium">No Completed Tasks</p>
                    <p className="text-sm text-muted-foreground">Completed tasks will appear here</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ==================== ASSIGNED USERS TAB ==================== */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>View all users in your organization</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users..."
                      value={usersTabSearchQuery}
                      onChange={(e) => setUsersTabSearchQuery(e.target.value)}
                      className="pl-10 w-48"
                      data-testid="users-tab-search"
                    />
                  </div>
                  {/* Toggle for assigned only */}
                  {assignedUsers.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Switch
                        id="show-assigned-only"
                        checked={usersTabShowOnlyAssigned}
                        onCheckedChange={setUsersTabShowOnlyAssigned}
                        data-testid="users-tab-assigned-toggle"
                      />
                      <Label htmlFor="show-assigned-only" className="text-sm cursor-pointer">
                        Only My Assigned
                      </Label>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                // Get the list of users to display
                const assignedUserIds = new Set(assignedUsers.map(u => u.user_id || u.id));
                let usersToShow = usersTabShowOnlyAssigned 
                  ? allUsers.filter(u => assignedUserIds.has(u.id))
                  : allUsers;
                
                // Apply search filter
                if (usersTabSearchQuery) {
                  const search = usersTabSearchQuery.toLowerCase();
                  usersToShow = usersToShow.filter(u => 
                    u.name?.toLowerCase().includes(search) ||
                    u.email?.toLowerCase().includes(search) ||
                    (typeof u.role === 'string' && u.role.toLowerCase().includes(search)) ||
                    (typeof u.role_id === 'string' && u.role_id.toLowerCase().includes(search))
                  );
                }
                
                // Filter out admin roles for non-admin auditors
                if (!isAdmin) {
                  usersToShow = usersToShow.filter(u => 
                    u.role !== 'admin' && u.role !== 'owner' && 
                    u.role_id !== 'admin' && u.role_id !== 'owner'
                  );
                }
                
                if (usersToShow.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="font-medium">No Users Found</p>
                      <p className="text-sm text-muted-foreground">
                        {usersTabSearchQuery ? 'Try a different search term' : 
                         usersTabShowOnlyAssigned ? 'No users are assigned to you yet' : 
                         'No users available'}
                      </p>
                    </div>
                  );
                }
                
                return (
                  <>
                    <div className="mb-4 text-sm text-muted-foreground">
                      Showing {usersToShow.length} user{usersToShow.length !== 1 ? 's' : ''}
                      {usersTabShowOnlyAssigned ? ' (assigned to you)' : ''}
                    </div>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {usersToShow.slice(0, 50).map((userItem, idx) => {
                        const isAssigned = assignedUserIds.has(userItem.id);
                        return (
                          <Card key={`user-${userItem.id || idx}`} className={cn("bg-muted/30", isAssigned && "ring-1 ring-purple-500/30")}>
                            <CardContent className="p-4">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                  <AvatarImage src={getAvatarDisplayUrl(userItem)} />
                                  <AvatarFallback className="bg-primary/10">
                                    {userItem.name?.charAt(0) || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium truncate">{userItem.name || 'Unknown'}</p>
                                    {isAssigned && (
                                      <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                                        Assigned
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground truncate">{userItem.email}</p>
                                </div>
                              </div>
                              <div className="mt-3 flex items-center justify-between text-sm">
                                <Badge variant="outline" className="capitalize">
                                  {(userItem.role_id || userItem.role || 'user').replace(/_/g, ' ')}
                                </Badge>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/dashboard/user-session?userId=${userItem.id}`)}
                                  className="h-8"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                    {usersToShow.length > 50 && (
                      <p className="text-center text-sm text-muted-foreground mt-4">
                        Showing first 50 users. Use search to find specific users.
                      </p>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== ACTIVITY LOG TAB ==================== */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Activity Log</CardTitle>
              <CardDescription>Recent QA activities and actions</CardDescription>
            </CardHeader>
            <CardContent>
              {activities.length > 0 ? (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    {activities.map((activity, idx) => (
                      <div key={activity.id || idx} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50">
                        <div className={`p-2 rounded-full ${
                          activity.action === 'override_compliance' || activity.action === 'bulk_override_compliance' 
                            ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' 
                            : activity.action === 'reviewed_alert' 
                            ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' 
                            : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          {activity.action?.includes('override') ? <Edit className="h-4 w-4" /> :
                           activity.action?.includes('review') ? <CheckCircle2 className="h-4 w-4" /> :
                           <Activity className="h-4 w-4" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {activity.action?.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {activity.auditor_name || 'Unknown'} • {new Date(activity.created_at).toLocaleString()}
                          </p>
                          {activity.details?.reason && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              "{activity.details.reason}"
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12">
                  <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="font-medium">No Activity Yet</p>
                  <p className="text-sm text-muted-foreground">Activities will appear here as you review alerts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== PERFORMANCE TAB ==================== */}
        <TabsContent value="performance" className="space-y-4" data-testid="performance-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>
                {isAdmin && selectedAuditors.length > 0 && selectedAuditors.length < auditors.length
                  ? `Showing metrics for ${selectedAuditors.length} selected auditor(s)`
                  : 'Overall auditor performance and statistics'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {performanceLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : performance ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                  <Card 
                    className="bg-emerald-500/10 border-emerald-500/30 cursor-pointer hover:bg-emerald-500/20 transition-colors"
                    onClick={() => setPerformanceDrillDown('reviews')}
                    data-testid="perf-card-reviews"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">Total Reviews</p>
                          <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{performance.total_reviews || 0}</p>
                        </div>
                        <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Click to view details</p>
                    </CardContent>
                  </Card>

                  <Card 
                    className="bg-purple-500/10 border-purple-500/30 cursor-pointer hover:bg-purple-500/20 transition-colors"
                    onClick={() => setPerformanceDrillDown('overrides')}
                    data-testid="perf-card-overrides"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Overrides</p>
                          <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{performance.total_overrides || 0}</p>
                        </div>
                        <Edit className="h-8 w-8 text-purple-500" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Click to view details</p>
                    </CardContent>
                  </Card>

                  <Card 
                    className="bg-blue-500/10 border-blue-500/30 cursor-pointer hover:bg-blue-500/20 transition-colors"
                    onClick={() => setPerformanceDrillDown('avgTime')}
                    data-testid="perf-card-avgtime"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Avg. Review Time</p>
                          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{performance.avg_review_time || 'N/A'}</p>
                        </div>
                        <Clock className="h-8 w-8 text-blue-500" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Click to view details</p>
                    </CardContent>
                  </Card>

                  <Card 
                    className="bg-amber-500/10 border-amber-500/30 cursor-pointer hover:bg-amber-500/20 transition-colors"
                    onClick={() => setPerformanceDrillDown('compliance')}
                    data-testid="perf-card-compliance"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Compliance Rate</p>
                          <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{performance.compliance_rate ? `${performance.compliance_rate}%` : 'N/A'}</p>
                        </div>
                        <TrendingUp className="h-8 w-8 text-amber-500" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Click to view details</p>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="font-medium">No Performance Data</p>
                  <p className="text-sm text-muted-foreground">Start reviewing alerts to see your metrics</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Performance Drill-Down Dialog */}
          <Dialog open={!!performanceDrillDown} onOpenChange={() => setPerformanceDrillDown(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {performanceDrillDown === 'reviews' && <><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Reviews Details</>}
                  {performanceDrillDown === 'overrides' && <><Edit className="h-5 w-5 text-purple-500" /> Override Details</>}
                  {performanceDrillDown === 'avgTime' && <><Clock className="h-5 w-5 text-blue-500" /> Review Time Analysis</>}
                  {performanceDrillDown === 'compliance' && <><TrendingUp className="h-5 w-5 text-amber-500" /> Compliance Details</>}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {performanceDrillDown === 'reviews' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Reviews</p>
                        <p className="text-2xl font-bold">{performance?.total_reviews || 0}</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">This Period</p>
                        <p className="text-2xl font-bold">{performance?.reviews_this_period || performance?.total_reviews || 0}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Track your review activity over time. Each reviewed alert contributes to compliance tracking.
                    </p>
                  </div>
                )}
                {performanceDrillDown === 'overrides' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Overrides</p>
                        <p className="text-2xl font-bold">{performance?.total_overrides || 0}</p>
                      </div>
                      <div className="p-4 bg-emerald-500/10 rounded-lg">
                        <p className="text-sm text-emerald-600">Marked Compliant</p>
                        <p className="text-2xl font-bold text-emerald-700">{performance?.overrides_compliant || 0}</p>
                      </div>
                      <div className="p-4 bg-red-500/10 rounded-lg">
                        <p className="text-sm text-red-600">Marked Non-Compliant</p>
                        <p className="text-2xl font-bold text-red-700">{performance?.overrides_non_compliant || 0}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Override actions allow you to adjust compliance status based on your review of the call.
                    </p>
                  </div>
                )}
                {performanceDrillDown === 'avgTime' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Avg. Review Time</p>
                        <p className="text-2xl font-bold">{performance?.avg_review_time || 'N/A'}</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Fastest Review</p>
                        <p className="text-2xl font-bold">{performance?.fastest_review || 'N/A'}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Review time is calculated from when you open an alert to when you submit your review.
                    </p>
                  </div>
                )}
                {performanceDrillDown === 'compliance' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Compliance Rate</p>
                        <p className="text-2xl font-bold">{performance?.compliance_rate || 0}%</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Reviewed</p>
                        <p className="text-2xl font-bold">{performance?.total_reviews || 0}</p>
                      </div>
                    </div>
                    <div className="w-full bg-muted h-4 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${performance?.compliance_rate || 0}%` }}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Compliance rate shows the percentage of calls that meet compliance standards.
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPerformanceDrillDown(null)}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AlertReviewDialog
        open={reviewDialogOpen}
        onClose={() => { setReviewDialogOpen(false); setSelectedAlert(null); }}
        alert={selectedAlert}
        onReview={handleReviewAlert}
        onOverride={handleOverrideCompliance}
      />

      <BulkOverrideDialog
        open={bulkOverrideDialogOpen}
        onClose={() => setBulkOverrideDialogOpen(false)}
        selectedCount={selectedAlerts.length}
        onConfirm={handleBulkOverride}
      />

      <CreateTaskDialog
        open={createTaskDialogOpen}
        onClose={() => setCreateTaskDialogOpen(false)}
        auditors={auditors}
        allUsers={allUsers}
        tenantId={tenantId}
        currentUser={user}
        onTaskCreated={() => fetchTasks()}
      />

      <EditTaskDialog
        open={editTaskDialogOpen}
        onClose={() => { setEditTaskDialogOpen(false); setSelectedTask(null); }}
        task={selectedTask}
        auditors={auditors}
        allUsers={allUsers}
        tenantId={tenantId}
        onTaskUpdated={() => fetchTasks()}
      />

      <DeleteTaskDialog
        open={deleteTaskDialogOpen}
        onClose={() => { setDeleteTaskDialogOpen(false); setSelectedTask(null); }}
        task={selectedTask}
        onConfirm={handleConfirmDeleteTask}
      />
    </div>
  );
}
